import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import { Button, PrimaryButton } from "../../components/button";
import { FormControl, Select } from "../../components/form";
import { Icon } from "../../components/icon";
import { clearPendingOAuthRequest, getSession, isAuthed, setPendingOAuthRequest } from "../../session";
import { displayAlert } from "../../ui/alert";
import { runOperation } from "../../ui/operation";
import { LoginLayout } from "./login-layout";

const permissionDescriptions = {
    InferenceText: "Generate text with models and AI gateways",
    InferenceGenerations: "Generate images, audio, transcriptions, decisions and other media",
    InferenceRealtime: "Open realtime voice sessions",
    ResourcesRead: "Read AI gateways, chat clients, batch, skills, shell sandbox files and other resources",
    ResourcesWrite: "Create, edit and delete AI gateways, chat clients, batch, skills, shell sandbox files and other resources",
    RagRead: "Search and read documents of RAG collections",
    RagWrite: "Create, edit and delete RAG collections and documents",
    ManagementAnalytics: "Read balance, consumption and billing information",
    Account: "Manage payments, subscriptions, plan changes, notifications and other account-level operations",
    WebUtilities: "Search the web and fetch URLs",
    AccountIdentity: "Read your account name and e-mail"
};

const durationLabels = { 1: "1 day", 7: "7 days", 90: "90 days", 365: "1 year" };

export default function (ctx) {
    const tnonce = ctx.query.tnonce;
    const status = el.state.create("loading");
    const request = el.state.create(null);
    const secondsLeft = el.state.create(5);
    let durationSelect;

    if (!tnonce) {
        clearPendingOAuthRequest();
    }
    else {
        setPendingOAuthRequest(tnonce);

        if (!isAuthed()) {
            page.redirect("/login");
            return;
        }
    }

    function startCountdown(milliseconds) {
        const unlockAt = Date.now() + milliseconds;
        const tick = () => {
            secondsLeft.value = Math.max(0, Math.ceil((unlockAt - Date.now()) / 1000));
            if (secondsLeft.value > 0) setTimeout(tick, 250);
        };

        tick();
    }

    async function load() {
        const result = await api.getJson(`/api/v1/auth/oauth/${encodeURIComponent(tnonce)}`, { suppressError: true });
        if (!isAuthed()) return;

        if (!result?.data) {
            clearPendingOAuthRequest();
            status.value = "error";
            return;
        }

        request.value = result.data;
        status.value = "review";
        startCountdown(result.data.availableIn);
    }

    function finish(result) {
        clearPendingOAuthRequest();
        if (result?.data?.redirectUrl) {
            status.value = "redirecting";
            window.location.assign(result.data.redirectUrl);
        }
    }

    function getCaptchaToken(container) {
        if (!window.turnstile) {
            return Promise.reject(new Error("Turnstile did not load."));
        }

        let widgetId;
        return new Promise((resolve, reject) => {
            widgetId = turnstile.render(container, {
                sitekey: window.captchaSiteKey,
                action: "oauth_authorize",
                appearance: "interaction-only",
                callback: resolve,
                "error-callback": () => reject(new Error("Turnstile could not verify this browser.")),
                "expired-callback": () => reject(new Error("Turnstile token expired."))
            });
        }).finally(() => turnstile.remove(widgetId));
    }

    function authorize(form) {
        runOperation("Authorizing application...", async () => {
            const captchaToken = await getCaptchaToken(form.querySelector(".oauth-turnstile"));
            const result = await api.postJson(`/api/v1/auth/oauth/${encodeURIComponent(tnonce)}/authorize`, {
                captchaToken,
                duration: Number(durationSelect.value)
            });
            finish(result);
        }).catch(error => displayAlert({ title: "Authorization failed", message: error.message }));
    }

    function deny() {
        runOperation("Denying access...", async () => {
            finish(await api.postJson(`/api/v1/auth/oauth/${encodeURIComponent(tnonce)}/deny`, {}));
        });
    }

    function Review(data) {
        durationSelect = Select({
            value: "7",
            items: data.durations.map(days => ({ text: durationLabels[days] || `${days} days`, value: String(days) }))
        });

        return el("form.oauth-review", {
            onSubmit(event) {
                event.preventDefault();
                if (secondsLeft.value > 0) return;
                authorize(event.target);
            }
        },
            el("div.oauth-account",
                Icon("user-line"),
                el("span", "Signed in as ", el("strong", getSession().data?.name || getSession().data?.email || "your account"))
            ),
            el("section.oauth-block",
                el("h2", "Reason"),
                el("p.oauth-reason", data.reason)
            ),
            el("section.oauth-block",
                el("h2", "Requested permissions"),
                el("ul.oauth-permissions",
                    data.permissions.map(name =>
                        el("li",
                            Icon("checkbox-circle-line"),
                            el("span", permissionDescriptions[name] || name)
                        )
                    ),
                    el("li",
                        Icon("checkbox-circle-line"),
                        el("span", permissionDescriptions.AccountIdentity)
                    )
                )
            ),
            FormControl("Access duration", durationSelect),
            el("p.oauth-return",
                Icon("external-link-line"),
                el("span",
                    "You will be redirected to ", el("strong", data.returnHost), " (", el("span.oauth-return-url", data.returnUrl), ")",
                    ". You can revoke this access at any time in My account › Authorized applications.")
            ),
            el("div.oauth-turnstile"),
            el("div.oauth-actions",
                Button("Deny", { type: "button", onClick: deny }),
                el.state.listening([secondsLeft], () =>
                    PrimaryButton(
                        el("span", secondsLeft.value > 0 ? `Authorize (${secondsLeft.value})` : "Authorize"),
                        Icon("shield-check-line"),
                        { type: "submit", disabled: secondsLeft.value > 0 }
                    )
                )
            )
        );
    }

    return LoginLayout(
        el("div.login-form.oauth-authorize",
            {
                mount() {
                    if (!tnonce) {
                        status.value = "error";
                        return;
                    }

                    load().catch(() => {
                        status.value = "error";
                    });
                }
            },
            el.state.listening([status, request], () => {
                if (status.value === "loading") {
                    return el("div.login-form-message", { "aria-live": "polite" }, "Loading the authorization request...");
                }

                if (status.value === "redirecting") {
                    return el("div.login-form-message", { "aria-live": "polite" }, "Redirecting you back to the application...");
                }

                if (status.value === "error") {
                    return el.fragment(
                        el("header.login-form-header",
                            el("h1", "Authorize application")
                        ),
                        el("div.login-form-message.error",
                            { "aria-live": "polite" },
                            Icon("error-warning-line"),
                            "This authorization request is invalid or has expired. Start again from the application."
                        )
                    );
                }

                return el.fragment(
                    el("header.login-form-header",
                        el("h1", request.value.clientName),
                        el("p", "This application is requesting access to your AIVAX account.")
                    ),
                    Review(request.value)
                );
            })
        )
    );
}
